import { useUpdateTeacher } from "context/UpdateTeacherContext.jsx";
import {
  cancellationOptions,
  classLists,
} from "conts/profileConst.js";
import { useToast } from "hooks/index.js";
import { useCancellation } from "hooks/policyHooks/useCancellation";
import { useDiscountPrivate } from "hooks/policyHooks/useDiscountPrivate";
import { Fragment, useState } from "react";
import styled from "styled-components";
import { ButtonPrimary } from "ui/base/Button/ButtonStyled.js";
import { HelperText } from "ui/base/helperText/HelperText.jsx";
import { InputSignUp } from "ui/base/InputSignUp/InputSignUp.jsx";
import { SelectButton } from "ui/base/selectInput/SelectButton.jsx";
import { Tooltip } from "ui/base/tooltip/Tooltip.jsx";
import { PlusIcon } from "ui/icons/index.js";
import { PercentIcon } from "ui/icons/inputIcons/PercentIcon.jsx";
import { defaultThemeColors, fonts, themeColor } from "ui/theme";
import { numberOnly } from "utils/index.js";

const TEXT_DISCOUNT_POLICY =
  "You can offer Users discount on their first booking or if they book multiple classes at the same time.";

const TEXT_CANCELLATION_POLICY =
  "Cancellation policies are based on the start time of the class. Classes canceled by users within 60 minutes of the start of the class are not subject to refund.";

export const Policies = ({ onSubmit, loading }) => {
  const { formData, setFormData } = useUpdateTeacher();

  const [formDataErrors, setFormDataErrors] = useState({});

  const {
    getAvailableOptions,
    handleAddCancellationPolicy,
    handleRemoveCancellationPolicy,
    handlePolicyChange,
    cancellationErrors,
    validateCancellationPolicy,
  } = useCancellation(formData, setFormData);

  const {
    getAvailableDiscountPrivate,
    handleAddDiscountPrivatePolicy,
    handleRemoveDiscountPrivatePolicy,
    handleDiscountPrivatePolicyChange,
    discountPrivateErrors,
    validatePrivatePolicy,
  } = useDiscountPrivate(formData, setFormData);

  const onClick = () => {
    if (formData.profile_pic && formData.profile_pic.uri.length === 0) {
      useToast("error", "Step 1: Upload profile picture");
      return;
    }

    // Validate cancellation policy
    let submitErrors = {
      ...formDataErrors,
      ...validateCancellationPolicy(formData.cancellation_policy),
    };

    // Validate prices and policies for private class
    submitErrors = {
      ...submitErrors,

      ...validatePrivatePolicy(formData.discount_policy_for_private_class),
    };

    // Check for any errors
    const hasErrors = Object.values(submitErrors).some((error) => error !== "");

    if (hasErrors) {
      useToast("error", "Fill all the fields");
      return;
    }

    onSubmit();
  };

  return (
    <Wrapper>
      <div>
        <FormContainer>
          <RowContainer>
            <PolicyContainer>
              <Title>Cancellation Policy</Title>
              <PurpleText>{"(Optional)"}</PurpleText>
              <HelperText>
                <p>
                  {"Specify refund for clients who cancel class."}
                </p>
              </HelperText>

              <Spacer>
                {formData.cancellation_policy.map((policy, i) => (
                  <Fragment key={policy.policy}>
                    <FlexContainer>
                      <SelectButton
                        options={getAvailableOptions()}
                        value={cancellationOptions.find(
                          (option) => option.value === policy.policy
                        )}
                        onChange={(selectedOption) => {
                          if (selectedOption?.value === "delete") {
                            handleRemoveCancellationPolicy(policy.policy);
                            return;
                          }
                          handlePolicyChange(
                            "policy",
                            policy.policy,
                            selectedOption.value
                          );
                        }}
                        showDelete
                        bgColor="blue15"
                        valueColor="blue"
                        placeholderColor="blue"
                        borderColor="transparent"
                      />
                      <PaymentInputContainer>
                        <InputSignUp
                          type="number"
                          inputmode="numeric"
                          gap="0.5rem"
                          showIconWhenEmpty
                          icon={<PercentIcon />}
                          iconPosition={"right"}
                          centerInput
                          id={`percentage-${i}`}
                          onInput={numberOnly}
                          placeholder="of payment"
                          value={policy.percentage}
                          maxLength={2}
                          setValue={(e) =>
                            handlePolicyChange(
                              "percentage",
                              policy.policy,
                              e ? +e : e
                            )
                          }
                        />
                      </PaymentInputContainer>
                    </FlexContainer>

                    {cancellationErrors?.[policy.policy] ? (
                      <HelperText
                        color="pink"
                        textAlign="right"
                        style={{ paddingRight: "30px" }}
                      >
                        {cancellationErrors?.[policy.policy]}
                      </HelperText>
                    ) : null}
                  </Fragment>
                ))}
              </Spacer>
              {formData.cancellation_policy.length <
                cancellationOptions.length ? (
                <FlexContainer>
                  <IconContainer onClick={handleAddCancellationPolicy}>
                    <PlusIcon size={20} color="#554DF1" />
                  </IconContainer>
                  <HelperText
                    onClick={handleAddCancellationPolicy}
                    color="placeholder"
                    style={{ cursor: "pointer" }}
                  >
                    Add cancellation policy
                  </HelperText>
                  <div style={{ marginLeft: "auto" }}>
                    <Tooltip
                      text={TEXT_CANCELLATION_POLICY}
                      position="top"
                      id="cancellation_policy"
                    />
                  </div>
                </FlexContainer>
              ) : null}
            </PolicyContainer>

            <PolicyContainer>
              <Title>Discount Policy</Title>
              <PurpleText>{"(Optional)"}</PurpleText>
              <>
                <HelperText>
                  {`Specify discount policy terms.`}
                </HelperText>
                <Spacer>
                  {formData.discount_policy_for_private_class.map(
                    (discountPrivate, i) => (
                      <Fragment key={discountPrivate.policy}>
                        <FlexContainer>
                          <SelectButton
                            options={getAvailableDiscountPrivate()}
                            value={classLists.find(
                              (option) =>
                                option.value === discountPrivate.policy
                            )}
                            onChange={(selectedOption) => {
                              if (selectedOption?.value === "delete") {
                                handleRemoveDiscountPrivatePolicy(
                                  discountPrivate.policy
                                );
                                return;
                              }
                              handleDiscountPrivatePolicyChange(
                                "policy",
                                discountPrivate.policy,
                                selectedOption.value
                              );
                            }}
                            showDelete
                            bgColor="blue15"
                            valueColor="blue"
                            placeholderColor="blue"
                            borderColor="transparent"
                          />
                          <PaymentInputContainer>
                            <InputSignUp
                              type="number"
                              inputmode="numeric"
                              icon={<PercentIcon />}
                              iconPosition={"right"}
                              showIconWhenEmpty
                              centerInput
                              id={`percentage-${i}`}
                              gap="0.5rem"
                              onInput={numberOnly}
                              placeholder="of payment"
                              value={discountPrivate.percentage}
                              maxLength={2}
                              setValue={(e) =>
                                handleDiscountPrivatePolicyChange(
                                  "percentage",
                                  discountPrivate.policy,
                                  e ? +e : e
                                )
                              }
                            />
                          </PaymentInputContainer>
                        </FlexContainer>

                        {discountPrivateErrors?.[discountPrivate.policy] ? (
                          <HelperText
                            color="pink"
                            textAlign="right"
                            style={{ paddingRight: "30px" }}
                          >
                            {discountPrivateErrors?.[discountPrivate.policy]}
                          </HelperText>
                        ) : null}
                      </Fragment>
                    )
                  )}
                </Spacer>
                {formData.discount_policy_for_private_class.length <
                  classLists.length ? (
                  <FlexContainer>
                    <IconContainer onClick={handleAddDiscountPrivatePolicy}>
                      <PlusIcon size={20} color="#554DF1" />
                    </IconContainer>
                    <HelperText
                      onClick={handleAddDiscountPrivatePolicy}
                      color="placeholder"
                      style={{ cursor: "pointer" }}
                    >
                      Add discount policy
                    </HelperText>

                    <div style={{ marginLeft: "auto" }}>
                      <Tooltip
                        text={TEXT_DISCOUNT_POLICY}
                        position="top"
                        id="discount_private"
                      />
                    </div>
                  </FlexContainer>
                ) : null}
              </>
            </PolicyContainer>
          </RowContainer>
        </FormContainer>
        <InputContainer>
          <div style={{ width: "100%", display: "flex" }}>
            <ButtonPrimary onClick={onClick} disabled={loading}>
              Save Changes
            </ButtonPrimary>
          </div>
        </InputContainer>
      </div>
    </Wrapper>
  );
};

const Title = styled.h3`
  font-family: Montserrat;
  font-weight: 600;
  font-size: ${fonts.sizes.h5};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: auto;

  & > div {
    display: grid;
    gap: 1rem;
  }
`;

const FormContainer = styled.div`
  display: grid;
  gap: 1rem;
  grid-column: span 2;
  height: fit-content;
  @media (min-width: 1024px) {
    grid-column: span 1;
  }
`;

const InputContainer = styled.div`
  grid-column: span 2;
`;

const PolicyContainer = styled.div`
  display: grid;
  gap: 1rem;
  height: min-content;
`;

const RowContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 2rem;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const IconContainer = styled.div`
  height: ${({ size }) => size ?? "45px"};
  width: ${({ size }) => size ?? "45px"};
  background-color: ${({ backgroundColor }) =>
    backgroundColor ?? defaultThemeColors.lightBlue};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 99px;
  color: ${({ color }) => color ?? defaultThemeColors.white};
`;
const FlexContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
`;

const PaymentInputContainer = styled.div`
  min-width: 142px;
  max-width: 160px;
`;

const Spacer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const PurpleText = styled.p`
  display: inline;
  color: ${themeColor("blue")};
`;